/**
 * @function useDeviceDetect
 * @author hzihhuang
 * @returns {DeviceFlags}
 * @description
 * 用于检测当前设备类型（PC / 平板 / 移动端），并在窗口宽度变化时自动更新设备类型状态。
 *
 * ✅ 特点：
 * 1. 自动监听 `html` 元素宽度变化，实时判断设备类型
 * 2. 使用 ResizeObserver，全局仅初始化一个监听器，避免重复监听
 * 3. 内置防抖机制，避免频繁触发更新,窗口尺寸停止变化后 500ms 更新一次，通常 pad、h5 下只运行一次
 * 4. 可自动响应窗口大小变化，适用于响应式布局判断
 *
 * - isPC: 是否为 PC（桌面设备，宽度 > 1024）
 * - isPad: 是否为平板（768 <= 宽度 <= 1024）
 * - isH5: 是否为移动端（宽度 < 768）
 *
 * 🧪 使用示例：
 *
 * ```tsx
 * import useDeviceDetect from '@/hooks/useDeviceDetect';
 *
 * const MyComponent = () => {
 *   const { isPC, isPad, isH5 } = useDeviceDetect();
 *
 *   return (
 *     <div>
 *       {isPC && <p>当前为桌面端</p>}
 *       {isPad && <p>当前为平板端</p>}
 *       {isH5 && <p>当前为移动端</p>}
 *     </div>
 *   );
 * };
 * ```
 * 📌 提示：
 * - 该 Hook 会在组件挂载时自动注册监听，卸载时自动移除
 * - 内部采用单例模式，不会对性能产生重复开销
 */

import { useState, useEffect } from 'react';

export interface DeviceFlags {
    isPC: boolean;
    isPad: boolean;
    isH5: boolean;
    type: 'pc' | 'pad' | 'h5';
}

type Listener = (flags: DeviceFlags) => void;

class DeviceDetector {
    private listeners: Set<Listener> = new Set(); // 订阅者集合
    private currentFlags: DeviceFlags | null = null; // 当前设备状态
    private resizeObserver: ResizeObserver | null = null; // ResizeObserver 实例
    private debounceTimeoutId: number | null = null; // 防抖定时器ID

    constructor() {
        this.handleResize = this.handleResize.bind(this);
        this.debouncedResizeHandler = this.debouncedResizeHandler.bind(this);
    }

    /**
     * 根据传入的「width」判断设备类型
     * @param width html 元素的宽度
     * @returns DeviceFlags 设备类型状态
     */
    private getDeviceFlags(width: number): DeviceFlags {
        const isPad = width >= 768 && width <= 1024;
        const isH5 = width < 768;
        const isPC = !isH5 && !isPad && width > 1024;
        const type = isPC ? 'pc' : isPad ? 'pad' : 'h5';
        return {
            isPC,
            isPad,
            isH5,
            type,
        };
    }
    /**
     * 通知所有订阅者当前设备状态变化
     */
    private notify() {
        if (this.currentFlags === null) return;
        this.listeners.forEach((listener) => listener(this.currentFlags as DeviceFlags));
    }

    /**
     * 真正处理 ResizeObserver 事件的函数（防抖后的调用）
     * @param entries ResizeObserverEntry 数组
     */
    private debouncedResizeHandler(entries: ResizeObserverEntry[]) {
        for (const entry of entries) {
            const width = entry.contentRect.width;
            const newFlags = this.getDeviceFlags(width);

            // 设备类型状态发生变化时更新并通知订阅者
            if (
                !this.currentFlags ||
                newFlags.isPC !== this.currentFlags.isPC ||
                newFlags.isPad !== this.currentFlags.isPad ||
                newFlags.isH5 !== this.currentFlags.isH5
            ) {
                this.currentFlags = newFlags;
                this.notify();
            }
        }
    }

    /**
     * ResizeObserver 回调，做防抖处理
     * @param entries ResizeObserverEntry 数组
     */
    private handleResize(entries: ResizeObserverEntry[]) {
        if (this.debounceTimeoutId !== null) {
            window.clearTimeout(this.debounceTimeoutId);
        }
        this.debounceTimeoutId = window.setTimeout(() => {
            this.debouncedResizeHandler(entries);
            this.debounceTimeoutId = null;
        }, 500);
    }

    /**
     * 开始监听 html 元素尺寸变化
     */
    private startObserving() {
        if (!this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(this.handleResize);
        }
        this.resizeObserver.observe(document.documentElement || document.body);
    }

    /**
     * 停止监听，断开 ResizeObserver，并清理防抖定时器
     */
    private stopObserving() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
            this.currentFlags = null;
        }

        if (this.debounceTimeoutId !== null) {
            window.clearTimeout(this.debounceTimeoutId);
            this.debounceTimeoutId = null;
        }
    }

    /**
     * 订阅设备状态变化
     * @param listener 监听函数，接收 DeviceFlags
     * @returns 取消订阅函数
     */
    subscribe(listener: Listener): () => void {
        this.listeners.add(listener);

        if (this.listeners.size === 1) {
            this.startObserving();

            if (!this.currentFlags) {
                const width = document.documentElement.clientWidth;
                this.currentFlags = this.getDeviceFlags(width);
            }
            listener(this.currentFlags);
        } else {
            // 其他订阅者直接同步当前状态
            listener(this.currentFlags as DeviceFlags);
        }

        // 返回取消订阅函数，移除监听者，最后一个取消时停止监听释放资源
        return () => {
            this.listeners.delete(listener);
            if (this.listeners.size === 0) {
                this.stopObserving();
            }
        };
    }
}

// 单例实例，避免重复监听
const deviceDetector = new DeviceDetector();

export default function useDeviceDetect(): DeviceFlags {
    const [deviceFlags, setDeviceFlags] = useState<DeviceFlags>({
        isPC: false,
        isPad: false,
        isH5: false,
        type: 'pc',
    });

    useEffect(() => {
        const unsubscribe = deviceDetector.subscribe(setDeviceFlags);
        return () => unsubscribe();
    }, []);

    return deviceFlags;
}
